/**
 * Vehicle DNA — modal that displays the AI-generated statistical card
 * for the user's vehicle.
 *
 * Flow:
 *   open() → shows the modal with a loading spinner → calls dna.service
 *   → renders the stats + insights or a friendly error.
 *
 * The DNA result is cached in module memory for the session so re-opening
 * the modal doesn't burn another quota slot. The user can hit "🔄 רענן"
 * to force a fresh call.
 */

import * as service from './dna.service.js';
import { escape as esc } from '../../shared/escape.js';
import { createLogger } from '../../core/logger.js';
import { iconHtml as aiIconHtml } from '../ai/icon.js';

const log = createLogger('dna.view');

const OVERLAY_ID = 'vehicleDNAOverlay';

/** Cached result so re-opens are instant within the same session. */
let cached = null;

/** Open the DNA modal. */
export async function open() {
  const overlay = ensureModal();
  document.body.appendChild(overlay);
  overlay.classList.add('open');
  if (cached) {
    renderResult(cached);
  } else {
    void load(false);
  }
}

function close() {
  const overlay = document.getElementById(OVERLAY_ID);
  if (!overlay) return;
  overlay.classList.remove('open');
}

function ensureModal() {
  let ov = document.getElementById(OVERLAY_ID);
  if (ov) return ov;
  ov = document.createElement('div');
  ov.id = OVERLAY_ID;
  ov.className = 'dna-overlay';
  ov.innerHTML = `
    <aside class="dna-modal" role="dialog" aria-modal="true" aria-labelledby="dnaTitle">
      <header class="dna-header">
        <div class="dna-title-block">
          <span class="dna-avatar">${aiIconHtml(40)}</span>
          <div>
            <div class="dna-title" id="dnaTitle">🧬 DNA הרכב</div>
            <div class="dna-sub">השוואה לדגמים זהים בישראל</div>
          </div>
        </div>
        <div class="dna-actions">
          <button type="button" class="dna-refresh" aria-label="רענן" title="טען מחדש">🔄</button>
          <button type="button" class="dna-close" aria-label="סגור">✕</button>
        </div>
      </header>
      <div class="dna-body" id="dnaBody"></div>
    </aside>
  `;
  ov.addEventListener('click', (e) => { if (e.target === ov) close(); });
  ov.querySelector('.dna-close')?.addEventListener('click', close);
  ov.querySelector('.dna-refresh')?.addEventListener('click', () => { void load(true); });
  return ov;
}

async function load(force) {
  const body = document.getElementById('dnaBody');
  if (!body) return;
  body.innerHTML = `
    <div class="dna-loading">
      <div class="dna-loading-spinner"></div>
      <div class="dna-loading-text">מנתח את הרכב שלך מול 12,000+ רכבים דומים בישראל…</div>
    </div>
  `;
  try {
    if (force) cached = null;
    const { dna } = await service.getDNA();
    cached = dna;
    renderResult(dna);
  } catch (err) {
    log.warn('getDNA failed:', err?.message);
    renderError(String(err?.message || ''));
  }
}

function renderResult(dna) {
  const body = document.getElementById('dnaBody');
  if (!body) return;
  const stats = Array.isArray(dna?.stats) ? dna.stats : [];
  const insights = Array.isArray(dna?.insights) ? dna.insights : [];

  body.innerHTML = `
    <div class="dna-summary">${esc(String(dna?.summary || ''))}</div>

    ${stats.length ? `
      <div class="dna-section-title">📊 מספרים על הרכב שלך</div>
      <div class="dna-stats">
        ${stats.map((s) => `
          <div class="dna-stat">
            <div class="dna-stat-label">${esc(String(s.label || ''))}</div>
            <div class="dna-stat-value">${esc(String(s.value || ''))}</div>
            ${s.context ? `<div class="dna-stat-context">${esc(String(s.context))}</div>` : ''}
          </div>
        `).join('')}
      </div>
    ` : ''}

    ${insights.length ? `
      <div class="dna-section-title">💡 תובנות</div>
      <div class="dna-insights">
        ${insights.map((i) => `
          <div class="dna-insight">
            <div class="dna-insight-icon">${esc(String(i.icon || '💡'))}</div>
            <div class="dna-insight-body">
              <div class="dna-insight-title">${esc(String(i.title || ''))}</div>
              <div class="dna-insight-text">${esc(String(i.text || ''))}</div>
            </div>
          </div>
        `).join('')}
      </div>
    ` : ''}

    <div class="dna-footer-note">
      הנתונים הם הערכות מקצועיות לשוק הישראלי 2026, מבוססות על דגם הרכב שלך.
      לערכים מדויקים — בדוק ביד2, לוי יצחק, או במוסך מורשה.
    </div>
  `;
}

function renderError(msg) {
  const body = document.getElementById('dnaBody');
  if (!body) return;
  const friendly = /resource-exhausted|מכסה/i.test(msg)
    ? 'הגעת למכסה היומית של AutoMe AI. הניתוח יחזור להיות זמין בחצות.'
    : /no vehicle/i.test(msg)
    ? 'עדיין לא משויך רכב לחשבון שלך. הוסף רכב כדי לראות את ה-DNA שלו.'
    : 'אירעה שגיאה בטעינת ה-DNA. נסה שוב בעוד רגע.';
  body.innerHTML = `
    <div class="dna-error">
      <div class="dna-error-emoji">😕</div>
      <div class="dna-error-text">${esc(friendly)}</div>
      <button type="button" class="dna-retry">נסה שוב</button>
    </div>
  `;
  body.querySelector('.dna-retry')?.addEventListener('click', () => { void load(true); });
}
